import { transform } from 'lodash';
import pt from 'prop-types';
import { Component } from 'react';
import { SUBSCRIBE_CHANNEL } from '../constants';

interface CreateLocalizationProvider<HandlerNames extends readonly string[]> {
    subscribeChannelKey?: string;
    localizationHandlerNames?: HandlerNames;
}

interface LocalizationProviderProps<HandlerNames extends readonly string[]> {
    localizationHandlers: {
        [key in HandlerNames[number]]: (...args: any[]) => string;
    };
    languageCode: string;
    children: React.ReactNode;
}

type Subscribers = Set<() => void>;

export type LocalizationSubscribeContext = {
    [key in typeof SUBSCRIBE_CHANNEL]: (subscriber: () => void) => () => void;
};

export default <HandlerNames extends readonly string[]>({
    subscribeChannelKey = SUBSCRIBE_CHANNEL,
    localizationHandlerNames = [] as unknown as HandlerNames,
}: CreateLocalizationProvider<HandlerNames> = {}) => {
    const localizationPropTypes = transform(
        localizationHandlerNames as unknown as string[],
        (propTypes, name) => {
            propTypes[name] = pt.func.isRequired;
        },
        {}
    );

    return class LocalizationProvider extends Component<LocalizationProviderProps<HandlerNames>> {
        static propTypes = {
            // @ts-expect-error $TSFixMeError: Argument of type '{ [index: string]: unknown; }' is not assignable to parameter of type 'ValidationMap<any>'.
            localizationHandlers: pt.shape({ ...localizationPropTypes }),
            languageCode: pt.string.isRequired,
            children: pt.node,
        };

        static childContextTypes = {
            ...localizationPropTypes,
            [subscribeChannelKey]: pt.func,
        };

        subscribers: Subscribers = new Set();

        getChildContext(): LocalizationSubscribeContext {
            const handlers = transform(
                this.props.localizationHandlers,
                (acc, handler: (...args: any[]) => string, name) => {
                    acc[name] = (...args: any[]) => handler(this.props.languageCode, ...args);
                },
                {}
            );

            return {
                [SUBSCRIBE_CHANNEL]: this.subscribe,
                ...handlers,
            };
        }

        componentWillUnmount() {
            this.subscribers = new Set();
        }

        componentDidUpdate(prevProps: LocalizationProviderProps<HandlerNames>) {
            if (prevProps.languageCode !== this.props.languageCode) {
                this.triggerUpdate();
            }
        }

        subscribe = (func: () => void) => {
            this.subscribers.add(func);

            return () => {
                this.subscribers.delete(func);
            };
        };

        triggerUpdate = () => {
            for (const func of this.subscribers) {
                func();
            }
        };

        render() {
            return this.props.children;
        }
    };
};
